/**
 * One-off personal greetings — small popups for specific players on specific
 * occasions. Hardcoded entries; no admin UI, no scheduler. Shipped as an
 * experiment / token-of-affection layer (project's intergenerational soul —
 * see `lothar.vip`).
 *
 * Each entry fires at most once per browser via a localStorage flag set on
 * **open** (so a refresh-without-close still counts as shown — guards against
 * accidental re-firing). An optional `notBefore` (`"YYYY-MM-DD"`, compared to
 * the local date) gates an entry to a calendar occasion: before that day the
 * entry stays inert (no fire, no flag) even for a matching player.
 *
 * **Annual reuse:** to make a past greeting resurface next year, bump its `id`
 * (fresh id → fresh unset flag → fires again) and set `notBefore` to next
 * year's date — same body, a surprise the player already saw once. To add a
 * one-off: append to `GREETINGS` with a fresh id. To retire all of them:
 * delete the file and the renderGameStart call.
 */
import { confirmDialog } from "./confirm-dialog.js";

const MOTHERS_DAY_BODY =
  "Alles Gute zum Muttertag, ich hab dich lieb! \u{1F618}";

// Mother's Day 2027 = second Sunday of May. These already fired once in 2026;
// the bumped id + notBefore resurface them as a surprise next year.
const MOTHERS_DAY_2027 = "2027-05-09";

const GREETINGS = [
  {
    id: "mothersDay2027",
    playerName: "Kerstin",
    body: MOTHERS_DAY_BODY,
    acknowledgeLabel: "Danke",
    notBefore: MOTHERS_DAY_2027,
  },
  {
    id: "mothersDay2027Ulrike",
    playerName: "Ulrike",
    body: MOTHERS_DAY_BODY,
    acknowledgeLabel: "Danke",
    notBefore: MOTHERS_DAY_2027,
  },
  {
    id: "mothersDay2027Hilde",
    playerName: "Hilde",
    body: MOTHERS_DAY_BODY,
    acknowledgeLabel: "Danke",
    notBefore: MOTHERS_DAY_2027,
  },
  {
    id: "birthday2026Matthias",
    playerName: "Matthias",
    body: "Hi Pa, alles Liebe und Gute zum Geburtstag! Hoffe du hast einen schönen Tag, lass es dir gut gehen.",
    acknowledgeLabel: "Danke",
    notBefore: "2026-06-21",
  },
];

const SHOWN_KEY_PREFIX = "personalGreeting.";

/**
 * Show the first matching greeting for the current player, if any. Called
 * from `renderGameStart` — gated by the localStorage shown flag, so it fires
 * at most once per browser per entry across the entry's lifetime.
 */
export function maybeShowPersonalGreeting() {
  const playerName = readPlayerName();
  if (!playerName) return;
  for (const greeting of GREETINGS) {
    if (greeting.playerName !== playerName) continue;
    if (greeting.notBefore && todayLocalIso() < greeting.notBefore) continue;
    if (alreadyShown(greeting.id)) continue;
    markShown(greeting.id);
    confirmDialog({
      body: greeting.body,
      acknowledgeLabel: greeting.acknowledgeLabel,
    });
    return;
  }
}

// why: compare on local Y-M-D components, not `new Date(notBefore)` (UTC
// midnight), so a date gate flips at local midnight — not 2h late in German
// summer time. Zero-padded ISO strings compare correctly lexicographically.
function todayLocalIso() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function readPlayerName() {
  try {
    return localStorage.getItem("playerName");
  } catch {
    return null;
  }
}

function alreadyShown(id) {
  try {
    return localStorage.getItem(SHOWN_KEY_PREFIX + id) === "true";
  } catch {
    return false;
  }
}

function markShown(id) {
  try {
    localStorage.setItem(SHOWN_KEY_PREFIX + id, "true");
  } catch {
    // Quota / private-browsing — fine, the greeting just may re-fire next visit.
  }
}
